import { useState, useEffect } from "react";
import { Skeleton, Tooltip, InputNumber, Input, Select, Modal } from "antd";
import Swal from "sweetalert2";
import {
  DollarOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined, SaveOutlined,
} from "@ant-design/icons";
import { isSuperAdmin } from "../utils/role";
import { useActiveRateCard, useUpdateRateCard, useAllPlans } from "../hooks/useRateCard";

/**
 * Rate Card master — the single source every proposal price is derived from.
 *
 * Split across three tabs (Settings / Corporate / Attorney) because the config
 * is three unrelated things: one page of it all at once was unreadable.
 *
 * The whole config is replaced on save (the API takes no partial keys), so the
 * screen edits a local draft and sends it back complete. `plan_name` is display
 * only — it comes from the backend and is stripped before saving; `plan_id` is
 * the field that actually travels.
 */

/* Money and counts are formatted differently on purpose. A rupee sign on
   "included users" or "max marks" made the table read as if seats were priced
   in lakhs — those are quantities, not prices. */
const money = (n) =>
  n === null || n === undefined || n === "" ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;
const count = (n) =>
  n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString("en-IN");

/** A band with no upper bound is the catch-all, not missing data. */
const marksRange = (min, max) => {
  const from = count(min);
  if (max === null || max === undefined) return `${from}+`;
  return `${from} – ${count(max)}`;
};

const card = {
  background: "white", borderRadius: 16, border: "1px solid #f0f0f0",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden", marginBottom: 20,
};
const cardHead = {
  padding: "14px 20px", borderBottom: "1px solid #f0f0f0", background: "#fafbff",
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
};
const cardTitle = { fontSize: 15, fontWeight: 700, color: "#111827" };
const th = {
  padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap",
};
const td = { padding: "10px 16px", fontSize: 13, color: "#374151", whiteSpace: "nowrap" };
const fieldLabel = { fontSize: 11.5, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5 };
const gridBox = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 20, padding: 20,
};

/** A scalar field. `kind` decides both the input affordance and the read view. */
const Field = ({ label, value, onChange, editable, kind = "money", max, hint }) => (
  <div>
    <label style={fieldLabel}>{label}</label>
    {editable ? (
      <InputNumber
        value={value ?? null}
        onChange={onChange}
        min={0}
        max={max}
        style={{ width: "100%" }}
        prefix={kind === "money" ? "₹" : undefined}
        formatter={(v) => (v === "" || v === null || v === undefined ? "" : Number(v).toLocaleString("en-IN"))}
        parser={(v) => (v || "").replace(/[^\d.]/g, "")}
      />
    ) : (
      <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
        {kind === "percent" ? `${value ?? "—"}%` : kind === "money" ? money(value) : count(value)}
      </div>
    )}
    {hint && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{hint}</div>}
  </div>
);

/** A cell inside a band/tier table. */
const Cell = ({ value, onChange, editable, kind = "count", nullable, nullLabel = "and above" }) => {
  if (!editable) {
    if (value === null || value === undefined) {
      return <span style={{ color: "#9ca3af", fontStyle: "italic" }}>{nullable ? nullLabel : "—"}</span>;
    }
    return <span>{kind === "money" ? money(value) : count(value)}</span>;
  }
  return (
    <InputNumber
      value={value ?? null}
      onChange={onChange}
      min={0}
      style={{ width: kind === "money" ? 140 : 120 }}
      prefix={kind === "money" ? "₹" : undefined}
      // A blank field is a real value in the nullable columns.
      placeholder={nullable ? nullLabel : ""}
      formatter={(v) => (v === "" || v === null || v === undefined ? "" : Number(v).toLocaleString("en-IN"))}
      parser={(v) => (v || "").replace(/[^\d.]/g, "")}
    />
  );
};

/**
 * The plan behind a tier. Reading shows the plan's name; editing shows a picker,
 * so an unmapped tier can be fixed right here instead of hunting for an id.
 */
const PlanCell = ({ planId, planName, plans, editable, onChange }) => {
  if (editable) {
    return (
      <Select
        value={planId ?? null}
        onChange={onChange}
        placeholder="Select a plan"
        style={{ minWidth: 190 }}
        allowClear
        showSearch
        optionFilterProp="label"
        options={plans.map((p) => ({ value: p.id, label: p.name || p.plan_name || `Plan #${p.id}` }))}
      />
    );
  }
  if (planName) return <span style={{ fontWeight: 700, color: "#111827" }}>{planName}</span>;
  return (
    /* Without a plan the proposal for this tier prints no feature list, so it is
       flagged where someone can act on it. */
    <Tooltip title="No plan mapped — a proposal on this tier will have no feature list">
      <span style={{ background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
        ⚠ Map a plan
      </span>
    </Tooltip>
  );
};

/** Flat tiers price by the year; the table shows one monthly figure regardless. */
const TierPrice = ({ tier, editable, onMonthly, onAnnual }) => {
  if (editable) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Cell value={tier.monthly} kind="money" editable onChange={onMonthly} />
        <span style={{ fontSize: 10.5, color: "#9ca3af" }}>or annual</span>
        <Cell value={tier.annual} kind="money" editable onChange={onAnnual} />
      </div>
    );
  }
  if (tier.monthly !== null && tier.monthly !== undefined) return <span>{money(tier.monthly)}</span>;
  if (tier.annual) {
    return (
      <div>
        <div>{money(Math.round(tier.annual / 12))}</div>
        <div style={{ fontSize: 10.5, color: "#9ca3af" }}>({money(tier.annual)}/yr)</div>
      </div>
    );
  }
  return <span style={{ color: "#9ca3af" }}>—</span>;
};

/**
 * The marks range this segment will quote for. It sits at the top of the
 * segment's own tab rather than in Settings: it is a property of that segment,
 * and reading "Attorney — max marks" on a page about neither was confusing.
 */
const LimitsCard = ({ limits, editable, onChange }) => (
  <div style={card}>
    <div style={cardHead}>
      <span style={cardTitle}>Trademark limits</span>
      <span style={{ fontSize: 12, color: "#9ca3af" }}>
        The proposal form refuses a portfolio outside these
      </span>
    </div>
    <div style={gridBox}>
      <Field label="Min marks" value={limits?.min_marks} kind="count" editable={editable}
        onChange={(v) => onChange("min_marks", v)} />
      <Field label="Max marks" value={limits?.max_marks} kind="count" editable={editable}
        onChange={(v) => onChange("max_marks", v)} />
    </div>
  </div>
);

/* Shared by both segment editors, so they live beside them rather than inside
   the page component. */
const delBtn = {
  width: 30, height: 30, borderRadius: 7, border: "1px solid #fecaca", background: "#fef2f2",
  color: "#dc2626", cursor: "pointer", display: "inline-flex", alignItems: "center",
  justifyContent: "center", fontSize: 12,
};
const addBtn = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8,
  border: "1px dashed #c7d2fe", background: "#f5f3ff", color: "#5b48e8", fontSize: 12,
  fontWeight: 700, cursor: "pointer",
};

/**
 * Flat-tier pricing editor (the Corporate model): banded monthly or annual
 * prices, plus a formula for portfolios past the last band.
 *
 * `base` is where this segment lives in the config — ["corporate"] for the
 * built-in one, ["custom_segments", slug] for a custom one — so the same editor
 * serves both.
 */
const FlatTiersEditor = ({ seg, base, editable, plans, setAt, addRow, removeRow }) => (
  <>
            <>
              <div style={card}>
                <div style={cardHead}><span style={cardTitle}>Add-ons</span></div>
                <div style={gridBox}>
                  <Field label="Extra user cost / month" value={seg.extra_user_cost} editable={editable}
                    onChange={(v) => setAt([...base, "extra_user_cost"], v)} />
                </div>
              </div>

              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Tiers</span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>Priced by portfolio size</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: "#fafbff" }}>
                      <tr>
                        <th style={th}>Plan</th>
                        <th style={th}>Marks</th>
                        <th style={th}>Price / month</th>
                        <th style={th}>Onboarding</th>
                        <th style={th}>Included users</th>
                        {editable && <th style={{ ...th, textAlign: "center" }}>—</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(seg.tiers || []).map((t, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                          <td style={td}>
                            <PlanCell
                              planId={t.plan_id} planName={t.plan_name} plans={plans} editable={editable}
                              onChange={(v) => setAt([...base, "tiers", i, "plan_id"], v ?? null)}
                            />
                          </td>
                          <td style={td}>
                            {editable ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <Cell value={t.min_marks} editable onChange={(v) => setAt([...base, "tiers", i, "min_marks"], v)} />
                                <span style={{ color: "#9ca3af" }}>–</span>
                                <Cell value={t.max_marks} nullable editable onChange={(v) => setAt([...base, "tiers", i, "max_marks"], v)} />
                              </div>
                            ) : (
                              marksRange(t.min_marks, t.max_marks)
                            )}
                          </td>
                          <td style={td}>
                            <TierPrice
                              tier={t} editable={editable}
                              onMonthly={(v) => setAt([...base, "tiers", i, "monthly"], v)}
                              onAnnual={(v) => setAt([...base, "tiers", i, "annual"], v)}
                            />
                          </td>
                          <td style={td}><Cell value={t.onboarding} kind="money" editable={editable} onChange={(v) => setAt([...base, "tiers", i, "onboarding"], v)} /></td>
                          <td style={td}><Cell value={t.included_users} editable={editable} onChange={(v) => setAt([...base, "tiers", i, "included_users"], v)} /></td>
                          {editable && (
                            <td style={{ ...td, textAlign: "center" }}>
                              <Tooltip title="Remove tier">
                                <button style={delBtn} onClick={() => removeRow([...base, "tiers"], i)}><DeleteOutlined /></button>
                              </Tooltip>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {editable && (
                  <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
                    <button style={addBtn} onClick={() => addRow([...base, "tiers"], { plan_id: null, min_marks: 0, max_marks: null, monthly: 0, onboarding: 0, included_users: 1 })}>
                      <PlusOutlined /> Add tier
                    </button>
                  </div>
                )}
              </div>

              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Enterprise formula</span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    Above {count(seg.enterprise_formula?.threshold)} marks the price is calculated, not banded
                  </span>
                </div>
                <div style={gridBox}>
                  <div>
                    <label style={fieldLabel}>Plan</label>
                    <PlanCell
                      planId={seg.enterprise_formula?.plan_id}
                      planName={seg.enterprise_formula?.plan_name}
                      plans={plans} editable={editable}
                      onChange={(v) => setAt([...base, "enterprise_formula", "plan_id"], v ?? null)}
                    />
                  </div>
                  <Field label="Threshold (marks)" value={seg.enterprise_formula?.threshold} kind="count" editable={editable}
                    onChange={(v) => setAt([...base, "enterprise_formula", "threshold"], v)} />
                  <Field label="Base annual" value={seg.enterprise_formula?.base_annual} editable={editable}
                    onChange={(v) => setAt([...base, "enterprise_formula", "base_annual"], v)} />
                  <Field label="Per mark" value={seg.enterprise_formula?.per_mark} editable={editable}
                    onChange={(v) => setAt([...base, "enterprise_formula", "per_mark"], v)} />
                  <Field label="Onboarding" value={seg.enterprise_formula?.onboarding} editable={editable}
                    onChange={(v) => setAt([...base, "enterprise_formula", "onboarding"], v)} />
                  <Field label="Max marks" value={seg.enterprise_formula?.max_marks} kind="count" editable={editable}
                    onChange={(v) => setAt([...base, "enterprise_formula", "max_marks"], v)} />
                  <Field label="Included users" value={seg.enterprise_formula?.included_users} kind="count" editable={editable}
                    onChange={(v) => setAt([...base, "enterprise_formula", "included_users"], v)} />
                </div>
              </div>
            </>
  </>
);

/**
 * Per-trademark pricing editor (the Attorney model): rate bands that taper with
 * portfolio size, plan bands for seats and clients, and a monotonic floor.
 */
const PerTmEditor = ({ seg, base, editable, plans, setAt, addRow, removeRow }) => (
  <>
              <div style={card}>
                <div style={cardHead}><span style={cardTitle}>Base &amp; add-ons</span></div>
                <div style={gridBox}>
                  <Field label="Floor / month" value={seg.floor_monthly} editable={editable}
                    onChange={(v) => setAt([...base, "floor_monthly"], v)} />
                  <Field label="Extra seat cost" value={seg.extra_seat_cost} editable={editable}
                    onChange={(v) => setAt([...base, "extra_seat_cost"], v)} />
                  <Field label="Extra client cost" value={seg.extra_client_cost} editable={editable}
                    onChange={(v) => setAt([...base, "extra_client_cost"], v)} />
                </div>
              </div>

              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Per-mark rate bands</span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>A bigger book pays less per mark</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: "#fafbff" }}>
                      <tr>
                        <th style={th}>Up to marks</th>
                        <th style={th}>Rate ₹/mark/yr</th>
                        {editable && <th style={{ ...th, textAlign: "center" }}>—</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(seg.rate_bands || []).map((b, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                          <td style={td}><Cell value={b.max_marks} nullable editable={editable} onChange={(v) => setAt([...base, "rate_bands", i, "max_marks"], v)} /></td>
                          <td style={td}><Cell value={b.rate} kind="money" editable={editable} onChange={(v) => setAt([...base, "rate_bands", i, "rate"], v)} /></td>
                          {editable && (
                            <td style={{ ...td, textAlign: "center" }}>
                              <button style={delBtn} onClick={() => removeRow([...base, "rate_bands"], i)}><DeleteOutlined /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {editable && (
                  <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
                    <button style={addBtn} onClick={() => addRow([...base, "rate_bands"], { max_marks: null, rate: 0 })}>
                      <PlusOutlined /> Add rate band
                    </button>
                  </div>
                )}
              </div>

              <div style={card}>
                <div style={cardHead}><span style={cardTitle}>Plan bands</span></div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: "#fafbff" }}>
                      <tr>
                        <th style={th}>Plan</th>
                        <th style={th}>Up to marks</th>
                        <th style={th}>Included seats</th>
                        <th style={th}>Free clients</th>
                        {editable && <th style={{ ...th, textAlign: "center" }}>—</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(seg.plan_bands || []).map((b, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                          <td style={td}>
                            <PlanCell
                              planId={b.plan_id} planName={b.plan_name} plans={plans} editable={editable}
                              onChange={(v) => setAt([...base, "plan_bands", i, "plan_id"], v ?? null)}
                            />
                          </td>
                          <td style={td}><Cell value={b.max_marks} nullable editable={editable} onChange={(v) => setAt([...base, "plan_bands", i, "max_marks"], v)} /></td>
                          <td style={td}><Cell value={b.included_users} editable={editable} onChange={(v) => setAt([...base, "plan_bands", i, "included_users"], v)} /></td>
                          {/* null free_clients means unlimited, not zero. */}
                          <td style={td}>
                            {editable ? (
                              <Cell value={b.free_clients} nullable nullLabel="Unlimited" editable onChange={(v) => setAt([...base, "plan_bands", i, "free_clients"], v)} />
                            ) : b.free_clients === null || b.free_clients === undefined ? (
                              <span style={{ color: "#16a34a", fontWeight: 600 }}>Unlimited</span>
                            ) : (
                              count(b.free_clients)
                            )}
                          </td>
                          {editable && (
                            <td style={{ ...td, textAlign: "center" }}>
                              <button style={delBtn} onClick={() => removeRow([...base, "plan_bands"], i)}><DeleteOutlined /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {editable && (
                  <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
                    <button style={addBtn} onClick={() => addRow([...base, "plan_bands"], { plan_id: null, max_marks: null, included_users: 1, free_clients: null })}>
                      <PlusOutlined /> Add plan band
                    </button>
                  </div>
                )}
              </div>

              {/* Secondary: a guard rail, not something anyone tunes daily. */}
              <details style={{ ...card, padding: 0 }}>
                <summary style={{ ...cardHead, cursor: "pointer", borderBottom: "none", listStyle: "revert" }}>
                  <span style={cardTitle}>Monotonic ceilings</span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    Annual floor per size, so a bigger book is never cheaper
                  </span>
                </summary>
                <div style={{ padding: "4px 20px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {(seg.monotonic_ceilings || []).map((pair, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                      <Cell value={pair[0]} editable={editable} onChange={(v) => setAt([...base, "monotonic_ceilings", i, 0], v)} />
                      <span style={{ color: "#9ca3af" }}>marks →</span>
                      <Cell value={pair[1]} kind="money" editable={editable} onChange={(v) => setAt([...base, "monotonic_ceilings", i, 1], v)} />
                      {editable && (
                        <button style={delBtn} onClick={() => removeRow([...base, "monotonic_ceilings"], i)}><DeleteOutlined /></button>
                      )}
                    </div>
                  ))}
                  {editable && (
                    <button style={{ ...addBtn, alignSelf: "flex-start" }} onClick={() => addRow([...base, "monotonic_ceilings"], [0, 0])}>
                      <PlusOutlined /> Add ceiling
                    </button>
                  )}
                </div>
              </details>
  </>
);

const TABS = [
  { key: "settings", label: "Settings" },
  { key: "corporate", label: "Corporate" },
  { key: "attorney", label: "Attorney" },
];

/* corporate and attorney live at the top level of the config, so a custom
   segment can never take those names. */
const RESERVED_SLUGS = ["corporate", "attorney", "settings"];

const slugify = (name) =>
  String(name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/** The empty shape a new segment starts from, per pricing model. */
const segmentSkeleton = (label, model) => {
  const common = { label, model, limits: { min_marks: 1, max_marks: null } };
  return model === "per_tm"
    ? {
        ...common,
        floor_monthly: 0, extra_seat_cost: 0, extra_client_cost: 0,
        rate_bands: [], plan_bands: [], monotonic_ceilings: [],
      }
    : {
        ...common,
        extra_user_cost: 0, tiers: [],
        enterprise_formula: {
          plan_id: null, threshold: null, base_annual: 0, per_mark: 0,
          onboarding: 0, max_marks: null, included_users: 1,
        },
      };
};

/** Name → slug → pricing model. The slug is the key the whole config hangs on. */
const AddSegmentModal = ({ existing, onClose, onCreate, saving }) => {
  const [label, setLabel] = useState("");
  const [slug, setSlug] = useState("");
  const [model, setModel] = useState("flat_tiers");
  const [error, setError] = useState("");

  // The slug follows the name until someone edits it by hand.
  const [slugTouched, setSlugTouched] = useState(false);
  const effectiveSlug = slugTouched ? slug : slugify(label);

  const submit = () => {
    if (!label.trim()) return setError("Give the segment a name");
    if (!effectiveSlug) return setError("The name must contain letters or numbers");
    if (RESERVED_SLUGS.includes(effectiveSlug)) return setError(`"${effectiveSlug}" is reserved`);
    if (existing.includes(effectiveSlug)) return setError("A segment with this slug already exists");
    setError("");
    onCreate(effectiveSlug, segmentSkeleton(label.trim(), model));
  };

  const modelBtn = (key, title, sub) => (
    <button
      key={key}
      onClick={() => setModel(key)}
      style={{
        textAlign: "left", padding: "12px 14px", borderRadius: 10, cursor: "pointer",
        border: `1.5px solid ${model === key ? "#5b48e8" : "#e5e7eb"}`,
        background: model === key ? "#f5f3ff" : "#fff",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: model === key ? "#5b48e8" : "#111827" }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>{sub}</div>
    </button>
  );

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>Add segment</span>}
      onCancel={onClose}
      onOk={submit}
      okText={saving ? "Creating…" : "Create segment"}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={460}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <label style={fieldLabel}>Name</label>
          <Input
            value={label}
            onChange={(e) => { setLabel(e.target.value); setError(""); }}
            placeholder="e.g. Reseller"
          />
        </div>
        <div>
          <label style={fieldLabel}>Slug</label>
          <Input
            value={effectiveSlug}
            onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); setError(""); }}
          />
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 5 }}>
            This is what the proposal form sends as <code>segment</code>. It cannot be changed later
            without breaking existing quotes.
          </div>
        </div>
        <div>
          <label style={fieldLabel}>Pricing model</label>
          <div style={{ display: "grid", gap: 8 }}>
            {modelBtn("flat_tiers", "Flat tiers", "Banded monthly or annual price, like Corporate")}
            {modelBtn("per_tm", "Per trademark", "Rate tapers with portfolio size, like Attorney")}
          </div>
        </div>
        {error && <div style={{ color: "#dc2626", fontSize: 12.5 }}>{error}</div>}
      </div>
    </Modal>
  );
};

const RateCard = () => {
  const { data: rateCard, isLoading, refetch, isFetching } = useActiveRateCard();
  const updateMutation = useUpdateRateCard();
  const mayEdit = isSuperAdmin();

  const [tab, setTab] = useState("settings");
  const [addingSegment, setAddingSegment] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [name, setName] = useState("");

  const { data: plans = [] } = useAllPlans(editing && mayEdit);

  /* The draft is a deep copy: editing must never mutate the query cache, or
     Cancel would have nothing to fall back to. */
  useEffect(() => {
    if (!rateCard) return;
    setDraft(JSON.parse(JSON.stringify(rateCard.config || {})));
    setName(rateCard.name || "");
  }, [rateCard]);

  if (isLoading || !draft) {
    return (
      <div className="main-content"><div className="page-content"><div className="container-fluid">
        <div style={{ ...card, padding: 24 }}><Skeleton active paragraph={{ rows: 8 }} /></div>
      </div></div></div>
    );
  }

  const editable = editing && mayEdit;
  const corporate = draft.corporate || {};
  const attorney = draft.attorney || {};
  const discounts = draft.discounts || {};
  const limits = draft.limits || {};
  const customSegments = draft.custom_segments || {};
  const customKeys = Object.keys(customSegments);
  const allTabs = [
    ...TABS,
    ...customKeys.map((k) => ({ key: k, label: customSegments[k]?.label || k, custom: true })),
  ];

  const setAt = (path, value) =>
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let node = next;
      for (let i = 0; i < path.length - 1; i += 1) {
        if (node[path[i]] === undefined) node[path[i]] = {};
        node = node[path[i]];
      }
      node[path[path.length - 1]] = value;
      return next;
    });

  const at = (path) => path.reduce((o, k) => o?.[k], draft);
  const addRow = (path, row) => setAt(path, [...(at(path) || []), row]);
  const removeRow = (path, idx) => setAt(path, (at(path) || []).filter((_, i) => i !== idx));

  /* plan_name is a convenience the backend adds for display. Sending it back
     would let a stale label overwrite the real one, so it is stripped. */
  const stripDisplayFields = (config) => {
    const clean = JSON.parse(JSON.stringify(config));
    (clean.corporate?.tiers || []).forEach((t) => delete t.plan_name);
    if (clean.corporate?.enterprise_formula) delete clean.corporate.enterprise_formula.plan_name;
    (clean.attorney?.plan_bands || []).forEach((b) => delete b.plan_name);
    // Custom segments carry the same display field on whichever list they use.
    Object.values(clean.custom_segments || {}).forEach((segment) => {
      (segment.tiers || []).forEach((t) => delete t.plan_name);
      (segment.plan_bands || []).forEach((b) => delete b.plan_name);
      if (segment.enterprise_formula) delete segment.enterprise_formula.plan_name;
    });
    return clean;
  };

  const handleSave = () =>
    updateMutation.mutate(
      { id: rateCard.id, name: name.trim() || undefined, config: stripDisplayFields(draft) },
      { onSuccess: () => setEditing(false) },
    );

  /* Adding and deleting a segment change the tab strip itself, so they save
     straight away instead of waiting for an Edit session — otherwise a new tab
     would exist only in the browser. */
  const handleCreateSegment = (slug, skeleton) => {
    const next = { ...draft, custom_segments: { ...(draft.custom_segments || {}), [slug]: skeleton } };
    setDraft(next);
    updateMutation.mutate(
      { id: rateCard.id, name: name.trim() || undefined, config: stripDisplayFields(next) },
      {
        onSuccess: () => {
          setAddingSegment(false);
          setTab(slug);
          // Straight into editing: a fresh segment has no bands yet.
          if (mayEdit) setEditing(true);
        },
      },
    );
  };

  const handleDeleteSegment = (slug) => {
    const segment = draft.custom_segments?.[slug];
    Swal.fire({
      title: `Delete "${segment?.label || slug}"?`,
      html: `Quotes already sent keep their numbers, but nothing can be priced on <b>${slug}</b> again.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      const rest = { ...(draft.custom_segments || {}) };
      delete rest[slug];
      const next = { ...draft, custom_segments: rest };
      setDraft(next);
      updateMutation.mutate(
        { id: rateCard.id, name: name.trim() || undefined, config: stripDisplayFields(next) },
        { onSuccess: () => { setTab("settings"); setEditing(false); } },
      );
    });
  };

  const handleCancel = () =>
    Swal.fire({
      title: "Discard changes?",
      text: "Everything you edited since opening the editor will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, discard",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      setDraft(JSON.parse(JSON.stringify(rateCard.config || {})));
      setName(rateCard.name || "");
      setEditing(false);
    });

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 100%)", borderRadius: 16, padding: "26px 30px", marginBottom: 20, boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 54, height: 54, background: "rgba(255,255,255,.22)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, color: "white" }}>
                <DollarOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 23, fontWeight: 700 }}>Rate Card</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: 14 }}>
                  {rateCard.name} — every proposal price is derived from this
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Tooltip title="Refresh">
                <button
                  onClick={() => refetch()}
                  style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}
                >
                  <ReloadOutlined spin={isFetching} />
                </button>
              </Tooltip>
              {mayEdit && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  style={{ height: 40, padding: "0 20px", borderRadius: 10, border: "none", background: "white", color: "#5b48e8", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
                >
                  Edit rate card
                </button>
              )}
              {editable && (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={updateMutation.isPending}
                    style={{ height: 40, padding: "0 18px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.1)", color: "white", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    style={{ display: "flex", alignItems: "center", gap: 7, height: 40, padding: "0 20px", borderRadius: 10, border: "none", background: "white", color: "#5b48e8", fontWeight: 700, fontSize: 14, cursor: updateMutation.isPending ? "not-allowed" : "pointer" }}
                  >
                    <SaveOutlined /> {updateMutation.isPending ? "Saving…" : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>

          {!mayEdit && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "10px 16px", marginBottom: 18, fontSize: 13, color: "#92400e" }}>
              You can read the rate card, but only a superadmin can change it.
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {allTabs.map((t) => {
              const on = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  style={{
                    padding: "9px 22px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
                    cursor: "pointer", transition: "all .15s",
                    border: `1.5px solid ${on ? "#5b48e8" : "#e5e7eb"}`,
                    background: on ? "#5b48e8" : "#fff",
                    color: on ? "#fff" : "#6b7280",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
            {mayEdit && (
              <button
                onClick={() => setAddingSegment(true)}
                style={{
                  padding: "9px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                  cursor: "pointer", border: "1.5px dashed #c7d2fe", background: "#f5f3ff", color: "#5b48e8",
                }}
              >
                <PlusOutlined /> Add segment
              </button>
            )}
          </div>

          {/* ── SETTINGS ── */}
          {tab === "settings" && (
            <>
              <div style={card}>
                <div style={cardHead}>
                  <span style={cardTitle}>Tax &amp; discounts</span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>Applied to every quote</span>
                </div>
                <div style={gridBox}>
                  {editable && (
                    <div>
                      <label style={fieldLabel}>Rate card name</label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                  )}
                  <Field label="GST" value={draft.gst_percent} kind="percent" max={100} editable={editable}
                    onChange={(v) => setAt(["gst_percent"], v)} />
                  <Field label="Auto discount — quarterly" value={discounts.auto_quarterly} kind="percent" max={100} editable={editable}
                    onChange={(v) => setAt(["discounts", "auto_quarterly"], v)} />
                  <Field label="Auto discount — yearly" value={discounts.auto_yearly} kind="percent" max={100} editable={editable}
                    onChange={(v) => setAt(["discounts", "auto_yearly"], v)} />
                  <Field label="Max sales discount" value={discounts.max_sales} kind="percent" max={100} editable={editable}
                    onChange={(v) => setAt(["discounts", "max_sales"], v)} />
                  <Field label="Max onboarding discount" value={discounts.max_onboarding} kind="percent" max={100} editable={editable}
                    onChange={(v) => setAt(["discounts", "max_onboarding"], v)} />
                </div>
              </div>
            </>
          )}

          {/* ── CORPORATE ── */}
          {tab === "corporate" && (
            <>
              <LimitsCard
                limits={limits.corporate} editable={editable}
                onChange={(key, v) => setAt(["limits", "corporate", key], v)}
              />
              <FlatTiersEditor
                seg={corporate} base={["corporate"]} editable={editable} plans={plans}
                setAt={setAt} addRow={addRow} removeRow={removeRow}
              />
            </>
          )}

          {/* ── ATTORNEY ── */}
          {tab === "attorney" && (
            <>
              <LimitsCard
                limits={limits.attorney} editable={editable}
                onChange={(key, v) => setAt(["limits", "attorney", key], v)}
              />
              <PerTmEditor
                seg={attorney} base={["attorney"]} editable={editable} plans={plans}
                setAt={setAt} addRow={addRow} removeRow={removeRow}
              />
            </>
          )}

          {/* ── CUSTOM SEGMENTS ──
              A custom segment is priced by one of the two built-in models, so it
              reuses the very same editor rather than a third one. */}
          {customKeys.includes(tab) && (() => {
            const seg = customSegments[tab] || {};
            const base = ["custom_segments", tab];
            const Editor = seg.model === "per_tm" ? PerTmEditor : FlatTiersEditor;
            return (
              <>
                <div style={card}>
                  <div style={cardHead}>
                    <span style={cardTitle}>{seg.label || tab}</span>
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>
                      {seg.model === "per_tm" ? "Per-trademark pricing" : "Flat tier pricing"} ·
                      slug <code style={{ background: "#f5f3ff", color: "#5b48e8", borderRadius: 4, padding: "1px 6px" }}>{tab}</code>
                    </span>
                    {mayEdit && (
                      <button
                        onClick={() => handleDeleteSegment(tab)}
                        style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                      >
                        <DeleteOutlined /> Delete segment
                      </button>
                    )}
                  </div>
                  <div style={gridBox}>
                    <Field label="Min marks" value={seg.limits?.min_marks} kind="count" editable={editable}
                      onChange={(v) => setAt([...base, "limits", "min_marks"], v)} />
                    <Field label="Max marks" value={seg.limits?.max_marks} kind="count" editable={editable}
                      onChange={(v) => setAt([...base, "limits", "max_marks"], v)} />
                  </div>
                </div>
                <Editor
                  seg={seg} base={base} editable={editable} plans={plans}
                  setAt={setAt} addRow={addRow} removeRow={removeRow}
                />
              </>
            );
          })()}

        </div>
      </div>

      {addingSegment && (
        <AddSegmentModal
          existing={customKeys}
          saving={updateMutation.isPending}
          onClose={() => setAddingSegment(false)}
          onCreate={handleCreateSegment}
        />
      )}
    </div>
  );
};

export default RateCard;
